import { Accordion } from 'react-bootstrap';
import { useEffect } from 'react';
import {
  NavLink,
  useLocation,
} from 'react-router-dom';
import {
  FaTachometerAlt,
  FaCommentDots,
  FaBuilding,
  FaStore,
  FaMotorcycle,
  FaClipboardList,
  FaPaperPlane,
  FaStar,
  FaWallet,
  FaTags,
  FaFileAlt,
} from 'react-icons/fa';
import {
  OverlayScrollbarsComponent,
} from 'overlayscrollbars-react';
import 'overlayscrollbars/overlayscrollbars.css';
import { useSelector } from 'react-redux';

import useCompanyContext from '../../hooks/useCompanyContext';

const Sidebar = ({
  collapsed,
  setCollapsed,
  fixCollapsed,
  activeKey,
  setActiveKey,
}) => {
  const location = useLocation();

  const { user } = useSelector(
    (state) => state.auth,
  );

  const {
    currentCompanyID,
    hasSpecificCompany,
    buildCompanyUrl,
  } = useCompanyContext();

  /**
   * 普通用户：
   * 返回原始地址，不添加 companyId。
   *
   * 管理员：
   * 自动添加当前 companyId。
   *
   * 示例：
   * /motorcycles?companyId=0
   * /motorcycles?companyId=<uuid>
   */
  const companyLink = (path) =>
    buildCompanyUrl(path);

  useEffect(() => {
    if (window.innerWidth < 1200) {
      setCollapsed(true);
    }
  }, [
    location.pathname,
    setCollapsed,
  ]);

  const handleMouseEnter = () => {
    if (collapsed && fixCollapsed) {
      setCollapsed(false);
    }
  };
/*   console.log('Sidebar company context:', {
    role: user?.role,
    currentCompanyID,
    motorcycleUrl: buildCompanyUrl('/motorcycles'),
  }); */
  const handleMouseLeave = () => {
    if (!collapsed && fixCollapsed) {
      setCollapsed(true);
      setActiveKey(false);
    }
  };

  const getMainLinkClass = ({
    isActive,
  }) =>
    `d-flex align-items-center custom-nav-link ${isActive
      ? 'active-link'
      : ''
    }`;

  const getSubLinkClass = ({
    isActive,
  }) =>
    `ps-4 d-flex align-items-center custom-nav-link ${isActive
      ? 'active-link'
      : ''
    }`;

  return (
    <OverlayScrollbarsComponent
      options={{
        scrollbars: {
          autoHide: 'leave',
          autoHideDelay: 0,
          theme: 'os-theme-custom',
        },
      }}
      className={`sidebar ${collapsed
        ? 'collapsed'
        : ''
        }`}
      onMouseEnter={
        handleMouseEnter
      }
      onMouseLeave={
        handleMouseLeave
      }
    >
      {/* Dashboard */}
      <NavLink
        to={companyLink(
          '/dashboard',
        )}
        className={({
          isActive,
        }) =>
          `mt-2 ${getMainLinkClass({
            isActive,
          })}`
        }
      >
        <FaTachometerAlt
          size={20}
        />

        {!collapsed && (
          <span className="ms-2">
            Dashboard
          </span>
        )}
      </NavLink>

      {!collapsed && (
        <div className="m-2 text-muted subTitle">
          Details
        </div>
      )}

      {/* Current company / Companies */}
      {hasSpecificCompany ? (
        <NavLink
          to={companyLink(
            `/company/edit/${currentCompanyID}`,
          )}
          className={
            getMainLinkClass
          }
        >
          <FaBuilding size={20} />

          {!collapsed && (
            <span className="ms-2">
              Company
            </span>
          )}
        </NavLink>
      ) : (
        <NavLink
          to="/companies/list"
          className={
            getMainLinkClass
          }
        >
          <FaBuilding size={20} />

          {!collapsed && (
            <span className="ms-2">
              Companies
            </span>
          )}
        </NavLink>
      )}

      {/* Shops */}
      <Accordion
        activeKey={activeKey}
        onSelect={(key) =>
          setActiveKey(key)
        }
      >
        <Accordion.Item eventKey="0">
          <Accordion.Header>
            <FaStore size={20} />

            {!collapsed && (
              <span className="ms-2">
                Shops
              </span>
            )}
          </Accordion.Header>

          <Accordion.Body className="p-0">
            <NavLink
              to={companyLink(
                '/shops/list',
              )}
              className={
                getSubLinkClass
              }
            >
              {!collapsed &&
                'Shops'}
            </NavLink>

            <NavLink
              to={companyLink(
                '/dropoffs',
              )}
              className={
                getSubLinkClass
              }
            >
              {!collapsed &&
                'Dropoffs'}
            </NavLink>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      {/* Motorcycles */}
      <NavLink
        to={companyLink(
          '/motorcycles',
        )}
        className={
          getMainLinkClass
        }
      >
        <FaMotorcycle size={20} />

        {!collapsed && (
          <span className="ms-2">
            Motorcycles
          </span>
        )}
      </NavLink>

      {/* Discounts */}
      <NavLink
        to={companyLink(
          '/discounts/list',
        )}
        className={
          getMainLinkClass
        }
      >
        <FaTags size={20} />

        {!collapsed && (
          <span className="ms-2">
            Discounts
          </span>
        )}
      </NavLink>

      {!collapsed && (
        <div className="m-2 text-muted subTitle">
          Workplace
        </div>
      )}

      {/* Bookings */}
      <NavLink
        to={companyLink(
          '/bookings/list',
        )}
        className={
          getMainLinkClass
        }
      >
        <FaClipboardList
          size={20}
        />

        {!collapsed && (
          <span className="ms-2">
            Bookings
          </span>
        )}
      </NavLink>

      {/* Requests */}
      <NavLink
        to={companyLink(
          '/requests/list',
        )}
        className={
          getMainLinkClass
        }
      >
        <FaPaperPlane size={20} />

        {!collapsed && (
          <span className="ms-2">
            Requests
          </span>
        )}
      </NavLink>

      {/* Reviews */}
      <NavLink
        to={companyLink(
          '/reviews/list',
        )}
        className={
          getMainLinkClass
        }
      >
        <FaStar size={20} />

        {!collapsed && (
          <span className="ms-2">
            Reviews
          </span>
        )}
      </NavLink>

      {/* Accountings */}
      <NavLink
        to={companyLink(
          '/accountings',
        )}
        className={
          getMainLinkClass
        }
      >
        <FaWallet size={20} />

        {!collapsed && (
          <span className="ms-2">
            Accountings
          </span>
        )}
      </NavLink>

      {/* Admin section */}
      {user?.role === 'ADMIN' && (
        <>
          {!collapsed && (
            <div className="m-2 text-muted subTitle">
              Admin
            </div>
          )}

          {/* Global company management */}
          <Accordion
            activeKey={activeKey}
            onSelect={(key) =>
              setActiveKey(key)
            }
          >
            <Accordion.Item eventKey="4">
              <Accordion.Header>
                <FaBuilding size={20} />

                {!collapsed && (
                  <span className="ms-2">
                    Companies
                  </span>
                )}
              </Accordion.Header>

              <Accordion.Body className="p-0">
                <NavLink
                  to="/companies/list"
                  className={
                    getSubLinkClass
                  }
                >
                  {!collapsed &&
                    'Companies List'}
                </NavLink>

                <NavLink
                  to="/unverified-companies/list"
                  className={
                    getSubLinkClass
                  }
                >
                  {!collapsed &&
                    'Unverified List'}
                </NavLink>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          {/* Global motorcycle models */}
          <NavLink
            to="/models/list"
            className={
              getMainLinkClass
            }
          >
            <FaMotorcycle
              size={20}
            />

            {!collapsed && (
              <span className="ms-2">
                Models
              </span>
            )}
          </NavLink>

          {/* Codes */}
          <Accordion
            activeKey={activeKey}
            onSelect={(key) =>
              setActiveKey(key)
            }
          >
            <Accordion.Item eventKey="5">
              <Accordion.Header>
                <FaTags size={20} />

                {!collapsed && (
                  <span className="ms-2">
                    Codes
                  </span>
                )}
              </Accordion.Header>

              <Accordion.Body className="p-0">
                <NavLink
                  to={companyLink(
                    '/codes/promo',
                  )}
                  className={
                    getSubLinkClass
                  }
                >
                  {!collapsed &&
                    'Promo'}
                </NavLink>

                <NavLink
                  to={companyLink(
                    '/codes/credits',
                  )}
                  className={
                    getSubLinkClass
                  }
                >
                  {!collapsed &&
                    'Credits'}
                </NavLink>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          {/* Reports */}
          <Accordion
            activeKey={activeKey}
            onSelect={(key) =>
              setActiveKey(key)
            }
          >
            <Accordion.Item eventKey="6">
              <Accordion.Header>
                <FaFileAlt size={20} />

                {!collapsed && (
                  <span className="ms-2">
                    Report
                  </span>
                )}
              </Accordion.Header>

              <Accordion.Body className="p-0">
                <NavLink
                  to={companyLink(
                    '/reports/sales',
                  )}
                  className={
                    getSubLinkClass
                  }
                >
                  {!collapsed &&
                    'Sales'}
                </NavLink>

                <NavLink
                  to={companyLink(
                    '/reports/insurance',
                  )}
                  className={
                    getSubLinkClass
                  }
                >
                  {!collapsed &&
                    'Insurance'}
                </NavLink>

                <NavLink
                  to={companyLink(
                    '/reports/license',
                  )}
                  className={
                    getSubLinkClass
                  }
                >
                  {!collapsed &&
                    'License'}
                </NavLink>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </>
      )}

      {!collapsed && (
        <div className="m-2 text-muted subTitle">
          Support
        </div>
      )}

      {/* Global support page */}
      <NavLink
        to="/support/contact"
        className={({
          isActive,
        }) =>
          `${getMainLinkClass({
            isActive,
          })} mb-5`
        }
      >
        <FaCommentDots size={20} />

        {!collapsed && (
          <span className="ms-2">
            Contact us
          </span>
        )}
      </NavLink>
    </OverlayScrollbarsComponent>
  );
};

export default Sidebar;